import React, { useEffect, useState } from 'react'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs'
import { useMutation } from '@apollo/client'
import { UpdatePassword, UpdateProfile, useUser } from '../../../graphql'
import SuccessAlert from '../../../components/Alerts/SuccessAlert'
import FailAlert from '../../../components/Alerts/FailAlert'
import useStore from '../../../store/useStore'
import { t } from 'i18next'

const AccountSettings = () => {
  const clientData = useStore((state: any) => state.userData);

  // I did scroll to the top because when the component is render it show the bottom of the page because the layout
  useEffect(() => {
    window.scrollTo(0, 0)
  })

  const [initialLoading, setInitialLoading] = useState(true);

  const [user, { loading, data: dataUser }] = useUser();

  useEffect(() => {
    clientData &&
      (async () => {
        await user({
          variables: { userId: clientData.id }
        })

        setInitialLoading(false)
      })()

  }, [clientData]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (dataUser?.user) {
      setName(dataUser.user.name);
      setEmail(dataUser.user.email);
      setPhone(dataUser.user.phone);
    }
  }, [dataUser]);

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setnewPassword] = useState("")
  const [confirmPass, setConfirmPass] = useState("")

  // Alerts
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  // update
  const [updateUser, { loading: updateLoading }] = useMutation(UpdateProfile, {
    onError: (error) => {
      if (error) {
        setFailMsg(t("phoneUsed"))
        setFail(true);
        return
      }
    },
    onCompleted: () => {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  });

  const [updatePassword, { loading: updatePasswordLoading }] = useMutation(UpdatePassword, {
    onError: (error) => {
      // @ts-ignore
      const code = error.message;
      console.error(code);

      if (code === "password is incorrect") {
        setFail(true);
        setFailMsg(t("passwordIncorrect"));
        return
      }
    },
    onCompleted: () => {
      setSuccessMsg(t('passwordChangedSucc'));
      setSuccess(true);

      setCurrentPassword("")
      setnewPassword("")
      setConfirmPass("")
    }
  });

  const changePassword = () => {
    if (currentPassword === "") {
      setFail(true);
      setFailMsg(t('emptyCurrentPassword'));
      return
    }

    if (newPassword === "") {
      setFail(true);
      setFailMsg(t('emptyNewPassword'));
      return
    }

    if (confirmPass === "") {
      setFail(true);
      setFailMsg(t('emptyConfirmPassword'));
      return
    }

    if (newPassword !== confirmPass) {
      setFail(true);
      setFailMsg(t('passwordNotMatch'));
      return
    }

    updatePassword({
      variables: {
        updatePasswordId: clientData.id,
        content: {
          newPassword: newPassword,
          oldPassword: currentPassword
        }
      }
    })
  }

  const submit = () => {
    if (name === "") {
      setFail(true);
      setFailMsg(t('nameRequire'));
      return
    }
    if (phone === "") {
      setFail(true);
      setFailMsg(t('emptyPhone'));
      return
    }
    if (email === "") {
      setFail(true);
      setFailMsg(t('emailEmpty'));
      return
    }

    updateUser({
      variables: {
        updateProfileId: clientData.id,
        content: {
          email: email,
          name: name,
          phone: phone
        }
      }
    })
  }

  return (
    <section className='mx-6 my-4'>
      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      <div className='flex flex-col-reverse md:flex-row md:justify-between md:items-center'>
        <div>
          <h3 className='text-2xl font-semibold'>{t('accountSettings')}</h3>
        </div>
        <div>
          <Breadcrumbs />
        </div>
      </div>

      <div className='relative p-2 mt-3 bg-white rounded-md'>
        {
          initialLoading || loading || updateLoading &&
          <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
            <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
          </div>
        }

        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div>
            <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">{t('name')}</label>
            <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('name')} required />
          </div>
          <div>
            <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">{t('email')}</label>
            <input type="text" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t("email")} required />
          </div>
          <div>
            <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900">{t('phone')}</label>
            <input type="text" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t("phone")} required />
          </div>
        </div>
        <div className='flex justify-end pt-5'>
          <button onClick={() => submit()} className='inline-flex items-center justify-center px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 21h14a2 2 0 0 0 2-2V8a1 1 0 0 0-.29-.71l-4-4A1 1 0 0 0 16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2zm10-2H9v-5h6zM13 7h-2V5h2zM5 5h2v4h8V5h.59L19 8.41V19h-2v-5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v5H5z"></path>
            </svg>

            <span className="mx-2">
              {t('save')}
            </span>
          </button>
        </div>
      </div>

      <div className='relative p-2 mt-3 bg-white rounded-md'>
        {
          updatePasswordLoading &&
          <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
            <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
          </div>
        }
        <div>
          <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900">{t('currentPassword')}</label>
          <input type="text" id="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('currentPassword')} required />
        </div>

        <div className='grid grid-cols-1 gap-4 mt-2 md:grid-cols-2'>
          <div>
            <label htmlFor="newPass" className="block mb-2 text-sm font-medium text-gray-900">{t('newPassword')}</label>
            <input type="text" id="newPass" value={newPassword} onChange={(e) => setnewPassword(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('newPassword')} required />
          </div>
          <div>
            <label htmlFor="confPass" className="block mb-2 text-sm font-medium text-gray-900">{t('confirmPassword')}</label>
            <input type="text" id="confPass" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('confirmPassword')} required />
          </div>
        </div>
        <div className='flex justify-end pt-5'>
          <button onClick={() => changePassword()} className='inline-flex items-center justify-center px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 21h14a2 2 0 0 0 2-2V8a1 1 0 0 0-.29-.71l-4-4A1 1 0 0 0 16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2zm10-2H9v-5h6zM13 7h-2V5h2zM5 5h2v4h8V5h.59L19 8.41V19h-2v-5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v5H5z"></path>
            </svg>

            <span className="mx-2">
              {t('save')}
            </span>
          </button>
        </div>
      </div>
    </section>
  )
}

export default AccountSettings